'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './FlickeringGrid.module.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

type GridParams = {
  cols: number;
  rows: number;
  squares: Float32Array;
  opacityCaps: Float32Array;
  dpr: number;
};

export type FlickeringGridProps = {
  squareSize?: number;
  gridGap?: number;
  flickerChance?: number;
  color?: string;
  width?: number;
  height?: number;
  maxOpacity?: number;
  edgeMaxOpacity?: number;
  fadePower?: number;
  className?: string;
};

function colorToRgbaPrefix(color: string): string {
  if (typeof window === 'undefined') {
    return 'rgba(21, 57, 245,';
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'rgba(21, 57, 245,';

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return `rgba(${r}, ${g}, ${b},`;
}

function cellOpacityCap(
  col: number,
  row: number,
  cols: number,
  rows: number,
  centerMax: number,
  edgeMax: number,
  fadePower: number,
): number {
  const nx = (col + 0.5) / cols;
  const ny = (row + 0.5) / rows;
  const dx = (nx - 0.5) * 2;
  const dy = (ny - 0.5) * 2;
  const edgeFactor = Math.min(1, Math.hypot(dx, dy));
  const centerWeight = (1 - edgeFactor) ** fadePower;
  return edgeMax + (centerMax - edgeMax) * centerWeight;
}

export const FlickeringGrid: React.FC<FlickeringGridProps> = ({
  squareSize = 4,
  gridGap = 6,
  flickerChance = 0.15,
  color = 'rgb(21, 57, 245)',
  width,
  height,
  maxOpacity = 0.12,
  edgeMaxOpacity = 0.02,
  fadePower = 1.35,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  const rgbaPrefix = useMemo(() => colorToRgbaPrefix(color), [color]);

  const setupCanvas = useCallback(
    (canvas: HTMLCanvasElement, canvasWidth: number, canvasHeight: number): GridParams => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      const cols = Math.floor(canvasWidth / (squareSize + gridGap));
      const rows = Math.floor(canvasHeight / (squareSize + gridGap));
      const squares = new Float32Array(cols * rows);
      const opacityCaps = new Float32Array(cols * rows);

      for (let i = 0; i < cols; i += 1) {
        for (let j = 0; j < rows; j += 1) {
          const idx = i * rows + j;
          const cap = cellOpacityCap(
            i,
            j,
            cols,
            rows,
            maxOpacity,
            edgeMaxOpacity,
            fadePower,
          );
          opacityCaps[idx] = cap;
          squares[idx] = Math.random() * cap;
        }
      }

      return { cols, rows, squares, opacityCaps, dpr };
    },
    [squareSize, gridGap, maxOpacity, edgeMaxOpacity, fadePower],
  );

  const updateSquares = useCallback(
    (squares: Float32Array, opacityCaps: Float32Array, deltaTime: number) => {
      for (let i = 0; i < squares.length; i += 1) {
        if (Math.random() < flickerChance * deltaTime) {
          squares[i] = Math.random() * opacityCaps[i];
        }
      }
    },
    [flickerChance],
  );

  const drawGrid = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      cols: number,
      rows: number,
      squares: Float32Array,
      dpr: number,
    ) => {
      const canvasWidth = ctx.canvas.width;
      const canvasHeight = ctx.canvas.height;
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      for (let i = 0; i < cols; i += 1) {
        for (let j = 0; j < rows; j += 1) {
          const opacity = squares[i * rows + j];
          if (opacity <= 0.001) continue;
          ctx.fillStyle = `${rgbaPrefix}${opacity})`;
          ctx.fillRect(
            i * (squareSize + gridGap) * dpr,
            j * (squareSize + gridGap) * dpr,
            squareSize * dpr,
            squareSize * dpr,
          );
        }
      }
    },
    [rgbaPrefix, squareSize, gridGap],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let animationFrameId = 0;
    let gridParams: GridParams | null = null;
    let lastTime = 0;

    const updateCanvasSize = () => {
      const newWidth = width ?? container.clientWidth;
      const newHeight = height ?? container.clientHeight;
      if (newWidth <= 0 || newHeight <= 0) return;
      gridParams = setupCanvas(canvas, newWidth, newHeight);
    };

    const animate = (time: number) => {
      animationFrameId = window.requestAnimationFrame(animate);

      if (!isInView || !gridParams) return;

      const deltaTime = lastTime ? (time - lastTime) / 1000 : 0;
      lastTime = time;

      updateSquares(gridParams.squares, gridParams.opacityCaps, deltaTime);
      drawGrid(
        ctx,
        gridParams.cols,
        gridParams.rows,
        gridParams.squares,
        gridParams.dpr,
      );
    };

    updateCanvasSize();

    const resizeObserver = new ResizeObserver(updateCanvasSize);
    resizeObserver.observe(container);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        setIsInView(Boolean(entry?.isIntersecting));
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(canvas);

    animationFrameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [setupCanvas, updateSquares, drawGrid, width, height, isInView]);

  return (
    <div ref={containerRef} className={cx(styles.gridRoot, className)}>
      <canvas ref={canvasRef} className={styles.gridCanvas} aria-hidden="true" />
    </div>
  );
};
